import { useEffect, useState } from 'react'
import { catalogEndpoints } from '@/core/api/endpoints'
import { ApiError } from '@/core/api/fetchClient'
import type { CatMve } from '@/core/db/entities'
import { mveDtoToEntity } from '@/core/db/mappers'
import { gravarCache, lerCache } from './carreteiroCache'

/**
 * Caminhões que podem lançar carreteiro na obra, na data da ficha.
 *
 * Regra do SIGA web (lst_mve_chefe_equipe.sql): caminhão cujo destino da
 * transferência vigente é o chefe do projeto da obra na data, com contrato
 * vigente e todos os itens vigentes na forma de remuneração 'C'. O backend
 * resolve tudo em `/catalogs/mves?obra_id&dt`.
 *
 * Offline: usa a última lista buscada para a obra (cache_carreteiro). O
 * vínculo e o contrato são validados de novo no envio.
 */

interface MvesCache {
  dt: string
  items: CatMve[]
}

export interface CarreteiroMvesState {
  mves: CatMve[]
  loading: boolean
  error: string | null
  source: 'live' | 'cache' | null
  /** Mensagem do SIGA quando a obra não tem chefe na data (bloqueia). */
  semChefe: string | null
}

const VAZIO: CarreteiroMvesState = { mves: [], loading: false, error: null, source: null, semChefe: null }

export function useMvesCarreteiroPorObra(
  obraId: number | null | undefined,
  dt: string,
): CarreteiroMvesState {
  const [state, setState] = useState<CarreteiroMvesState>(VAZIO)

  useEffect(() => {
    if (obraId == null) {
      setState(VAZIO)
      return
    }
    let cancelled = false
    setState((s) => ({ ...s, loading: true, error: null, semChefe: null }))

    ;(async () => {
      const key = `mves:${obraId}`
      // Cache primeiro: com sinal fraco o fetch demora a desistir; a lista guardada aparece já.
      const previo = await lerCache<MvesCache>(key)
      if (cancelled) return
      if (previo) setState({ mves: previo.items, loading: true, error: null, source: 'cache', semChefe: null })
      try {
        const res = await catalogEndpoints.mvesPorObra(obraId, dt)
        if (cancelled) return
        const items = (res.data?.items ?? []).map(mveDtoToEntity)
        items.sort((a, b) => (a.prefixo ?? '').localeCompare(b.prefixo ?? ''))
        void gravarCache(key, { dt, items } satisfies MvesCache)
        setState({ mves: items, loading: false, error: null, source: 'live', semChefe: null })
      } catch (err) {
        if (cancelled) return
        if (err instanceof ApiError && err.code === 'OBRA_SEM_CHEFE') {
          setState({ ...VAZIO, source: 'live', semChefe: err.message })
          return
        }
        const msg = err instanceof Error ? err.message : String(err)
        const cache = await lerCache<MvesCache>(key)
        if (cancelled) return
        setState(
          cache
            ? { mves: cache.items, loading: false, error: msg, source: 'cache', semChefe: null }
            : { ...VAZIO, error: msg },
        )
      }
    })()

    return () => {
      cancelled = true
    }
  }, [obraId, dt])

  return state
}
